import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { startRegistration } from "@simplewebauthn/browser";
import { Check, KeyRound, LogIn, Settings as SettingsIcon } from "lucide-react";
import clsx from "clsx";
import { env } from "../../env.js";
import { api, ApiError } from "../../api/client.js";
import {
  useMe,
  useSetupAdmin,
  useSetupFinish,
  useSetupStatus,
} from "../../api/hooks.js";
import { Button, CenteredSpinner } from "../../components/ui.js";
import { toast } from "../../components/toast.js";
import { SiteConfigFields } from "../settings/SiteConfigFields.js";
import {
  siteConfigSchema,
  type SiteConfigForm,
} from "../settings/siteConfigForm.js";
import type { SetupFinishBody } from "../../api/types.js";

const PROVIDERS = [
  { id: "telegram", label: "Telegram" },
  { id: "github", label: "GitHub" },
  { id: "google", label: "Google" },
] as const;

function StepHeader({
  index,
  current,
  done,
  label,
  icon: Icon,
}: {
  index: number;
  current: number;
  done: boolean;
  label: string;
  icon: typeof LogIn;
}) {
  const active = index === current;
  return (
    <div className="flex items-center gap-2">
      <span
        className={clsx(
          "flex h-8 w-8 items-center justify-center rounded-full text-xs ring-1 ring-inset transition-colors",
          done
            ? "bg-emerald-500 text-white ring-emerald-500 shadow-[0_0_12px] shadow-emerald-500/40"
            : active
              ? "bg-primary/10 text-primary ring-primary shadow-glow"
              : "text-muted-foreground ring-white/10",
        )}
      >
        {done ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
      </span>
      <span
        className={clsx(
          "text-sm",
          active ? "font-medium text-foreground" : "text-muted-foreground",
        )}
      >
        {label}
      </span>
    </div>
  );
}

export function SetupWizard() {
  const navigate = useNavigate();
  const setupStatus = useSetupStatus();
  const me = useMe({ enabled: true });
  const setupAdmin = useSetupAdmin();
  const setupFinish = useSetupFinish();

  const [step, setStep] = useState(0);
  const [adminPromoted, setAdminPromoted] = useState(false);
  const [passkeyDone, setPasskeyDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const isAdmin = me.data?.role === "ADMIN";
  const loggedIn = !!me.data;

  // If setup is already complete, leave the wizard.
  useEffect(() => {
    if (setupStatus.data?.initialized) {
      navigate("/users", { replace: true });
    }
  }, [setupStatus.data?.initialized, navigate]);

  // If we returned from the auth dance already signed-in, promote to admin.
  async function promote() {
    setBusy(true);
    try {
      await setupAdmin.mutateAsync();
      await me.refetch();
      setAdminPromoted(true);
      setStep(1);
      toast("You are now an administrator", "success");
    } catch (e) {
      if (e instanceof ApiError) toast(e.message, "error");
    } finally {
      setBusy(false);
    }
  }

  function signIn(provider: string) {
    const next = encodeURIComponent(window.location.href);
    window.location.assign(
      `${env.apiBase}/auth/${provider}/start?next=${next}`,
    );
  }

  async function registerPasskey() {
    setBusy(true);
    try {
      const options = await api.post<Record<string, unknown>>(
        "/auth/passkey/register/start",
        {},
      );
      // @simplewebauthn/browser expects { optionsJSON } in v13.
      const attResp = await startRegistration({
        optionsJSON: options as never,
      });
      await api.post("/auth/passkey/register/finish", {
        response: attResp,
        label: "Admin passkey",
      });
      setPasskeyDone(true);
      setStep(2);
      toast("Passkey registered", "success");
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      toast(msg || "Passkey registration failed", "error");
    } finally {
      setBusy(false);
    }
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SiteConfigForm>({
    resolver: zodResolver(siteConfigSchema),
    defaultValues: {
      siteName: "maitv",
      streamEndGraceMinutes: 5,
      recordingRetentionDays: 30,
      s3Endpoint: "",
      s3Region: "",
      s3Bucket: "",
      s3PublicAssetBase: "",
      s3AccessKey: "",
      s3SecretKey: "",
    },
  });

  async function finish(values: SiteConfigForm) {
    const s3: NonNullable<SetupFinishBody["s3"]> = {};
    if (values.s3Endpoint) s3.endpoint = values.s3Endpoint;
    if (values.s3Region) s3.region = values.s3Region;
    if (values.s3Bucket) s3.bucket = values.s3Bucket;
    if (values.s3PublicAssetBase) s3.publicAssetBase = values.s3PublicAssetBase;
    if (values.s3AccessKey) s3.accessKey = values.s3AccessKey;
    if (values.s3SecretKey) s3.secretKey = values.s3SecretKey;

    const body: SetupFinishBody = {
      siteName: values.siteName,
      streamEndGraceMinutes: values.streamEndGraceMinutes,
      recordingRetentionDays: values.recordingRetentionDays,
      ...(Object.keys(s3).length ? { s3 } : {}),
    };
    try {
      await setupFinish.mutateAsync(body);
      const status = await setupStatus.refetch();
      if (status.data?.initialized) {
        toast("Setup complete", "success");
        navigate("/users", { replace: true });
      }
    } catch {
      /* toasted globally */
    }
  }

  if (setupStatus.isLoading) return <CenteredSpinner label="Loading setup…" />;

  return (
    <div className="flex min-h-screen items-start justify-center px-4 py-12">
      <div className="w-full max-w-2xl animate-fade-in">
        <div className="mb-8 text-center">
          <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary font-display text-xl font-bold text-primary-foreground shadow-glow ring-1 ring-white/20">
            m
          </span>
          <div className="eyebrow mb-2">First-run setup</div>
          <h1 className="bg-gradient-to-r from-foreground to-primary bg-clip-text font-display text-3xl font-semibold tracking-tight text-transparent">
            Welcome to maitv
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Create the first administrator to bring the control plane online.
          </p>
        </div>

        <div className="surface mb-6 flex items-center justify-between px-5 py-4">
          <StepHeader
            index={0}
            current={step}
            done={adminPromoted || isAdmin}
            label="Sign in"
            icon={LogIn}
          />
          <div className="mx-2 h-px flex-1 bg-white/10" />
          <StepHeader
            index={1}
            current={step}
            done={passkeyDone}
            label="Passkey"
            icon={KeyRound}
          />
          <div className="mx-2 h-px flex-1 bg-white/10" />
          <StepHeader
            index={2}
            current={step}
            done={false}
            label="Site config"
            icon={SettingsIcon}
          />
        </div>

        <div className="surface p-6">
          {/* Step 1: sign in + promote */}
          {step === 0 && (
            <div className="space-y-4">
              <h2 className="font-display text-base font-semibold text-foreground">
                Sign in to become admin
              </h2>
              {!loggedIn ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    Sign in with an external provider. The account you sign in
                    with will be promoted to administrator.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {PROVIDERS.map((p) => (
                      <Button
                        key={p.id}
                        variant="secondary"
                        onClick={() => signIn(p.id)}
                      >
                        Continue with {p.label}
                      </Button>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-foreground/80">
                    Signed in as{" "}
                    <span className="font-medium">
                      {me.data?.displayName}
                    </span>
                    . Promote this account to administrator to continue.
                  </p>
                  <Button
                    variant="primary"
                    onClick={promote}
                    loading={busy || setupAdmin.isPending}
                  >
                    Make me admin
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Step 2: passkey */}
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="font-display text-base font-semibold text-foreground">
                Secure with a passkey
              </h2>
              <p className="text-sm text-muted-foreground">
                Register a passkey so you can sign in directly next time without
                a third-party provider. Optional but recommended.
              </p>
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  onClick={registerPasskey}
                  loading={busy}
                >
                  <KeyRound className="h-4 w-4" /> Register passkey
                </Button>
                <Button variant="ghost" onClick={() => setStep(2)}>
                  Skip for now
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: site config */}
          {step === 2 && (
            <form onSubmit={handleSubmit(finish)} className="space-y-5">
              <h2 className="font-display text-base font-semibold text-foreground">
                Site configuration
              </h2>
              <SiteConfigFields register={register} errors={errors} />
              <div className="flex justify-end">
                <Button
                  type="submit"
                  variant="primary"
                  loading={setupFinish.isPending}
                >
                  Finish setup
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
